/* =============================================================
   THEME.JS — Celestial Theme v1.0
   ============================================================= */

const Theme = (() => {

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = window.matchMedia('(pointer: coarse)').matches;
  const isMobile = () => window.innerWidth <= 768;

  const body = document.body;
  const animationsEnabled = body.dataset.animationsEnabled !== 'false';
  const parallaxEnabled = body.dataset.parallaxEnabled !== 'false';

  // ============================================================
  // Scroll Reveal
  // ============================================================

  function initScrollReveal() {
    const elements = document.querySelectorAll('.reveal');

    if (prefersReducedMotion || !animationsEnabled) {
      elements.forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -40px 0px'
    });

    elements.forEach(el => observer.observe(el));
  }

  // ============================================================
  // Parallax
  // ============================================================

  class ParallaxSection {
    constructor(el) {
      this.el = el;
      this.bg = el.querySelector('[data-parallax-bg]');
      this.speed = parseFloat(el.dataset.parallaxSpeed) || 0.35;
      this.rafId = null;
      this.inView = false;

      if (!this.bg) return;

      this.tick = this.tick.bind(this);

      const io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.inView = entry.isIntersecting;
          if (this.inView) {
            this.rafId = requestAnimationFrame(this.tick);
          } else {
            cancelAnimationFrame(this.rafId);
          }
        });
      }, { threshold: 0 });

      io.observe(this.el);
    }

    tick() {
      if (!this.inView) return;
      const rect = this.el.getBoundingClientRect();
      const viewportMid = window.innerHeight / 2;
      const sectionMid = rect.top + rect.height / 2;
      const delta = (viewportMid - sectionMid) * this.speed;
      this.bg.style.transform = 'translateY(' + delta + 'px)';
      this.rafId = requestAnimationFrame(this.tick);
    }
  }

  function initParallax() {
    if (prefersReducedMotion || isTouch || isMobile() || !parallaxEnabled) return;

    document.querySelectorAll('[data-parallax-section]').forEach(el => {
      new ParallaxSection(el);
    });
  }

  // ============================================================
  // 3D Tilt
  // ============================================================

  class TiltCard {
    constructor(el) {
      this.el = el;
      this.inner = el.querySelector('.featured-artwork__media-inner') || el;
      this.maxDeg = parseFloat(el.dataset.tiltMax) || 5;
      this.active = true;

      this.boundMove = this.onMove.bind(this);
      this.boundLeave = this.onLeave.bind(this);

      this.el.addEventListener('pointermove', this.boundMove);
      this.el.addEventListener('pointerleave', this.boundLeave);
    }

    onMove(e) {
      if (!this.active) return;
      const rect = this.el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      const rotX = (-y * this.maxDeg).toFixed(2);
      const rotY = (x * this.maxDeg).toFixed(2);
      this.inner.style.transform = 'perspective(900px) rotateX(' + rotX + 'deg) rotateY(' + rotY + 'deg)';
    }

    onLeave() {
      this.inner.style.transition = 'transform 0.5s ease';
      this.inner.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg)';
      this.inner.addEventListener('transitionend', () => {
        this.inner.style.transition = '';
      }, { once: true });
    }
  }

  function initTilt() {
    if (prefersReducedMotion || isTouch || isMobile()) return;

    document.querySelectorAll('[data-tilt]').forEach(el => {
      new TiltCard(el);
    });
  }

  // ============================================================
  // Header Scroll Behavior
  // ============================================================

  function initHeader() {
    const header = document.querySelector('[data-header]');
    if (!header || !('transparent' in header.dataset)) return;

    const onScroll = () => {
      header.classList.toggle('header--scrolled', window.scrollY > 80);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ============================================================
  // Mobile Navigation
  // ============================================================

  function initMobileNav() {
    const hamburger = document.querySelector('[data-hamburger]');
    const nav = document.querySelector('[data-mobile-nav]');
    if (!hamburger || !nav) return;

    const open = () => {
      hamburger.setAttribute('aria-expanded', 'true');
      nav.setAttribute('aria-hidden', 'false');
      body.style.overflow = 'hidden';
    };

    const close = () => {
      hamburger.setAttribute('aria-expanded', 'false');
      nav.setAttribute('aria-hidden', 'true');
      body.style.overflow = '';
    };

    hamburger.addEventListener('click', () => {
      const isOpen = hamburger.getAttribute('aria-expanded') === 'true';
      isOpen ? close() : open();
    });

    nav.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', close);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && hamburger.getAttribute('aria-expanded') === 'true') {
        close();
        hamburger.focus();
      }
    });
  }

  // ============================================================
  // Cart Drawer
  // ============================================================

  function initCartDrawer() {
    if (body.dataset.cartType !== 'drawer') return;

    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    const closeBtn = drawer.querySelector('[data-cart-drawer-close]');
    const overlay = drawer.querySelector('[data-cart-drawer-overlay]');

    const openDrawer = () => {
      drawer.setAttribute('aria-hidden', 'false');
      body.style.overflow = 'hidden';
    };

    const closeDrawer = () => {
      drawer.setAttribute('aria-hidden', 'true');
      body.style.overflow = '';
    };

    document.querySelectorAll('[data-cart-link]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        openDrawer();
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
        closeDrawer();
      }
    });
  }

  // ============================================================
  // Product Page — Thumbnail Gallery
  // ============================================================

  function initProductGallery() {
    const featured = document.querySelector('.main-product__featured-image img');
    if (!featured) return;

    document.querySelectorAll('.main-product__thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.querySelector('img').src;
        const srcset = thumb.querySelector('img').getAttribute('srcset');

        featured.src = src.replace('width=200', 'width=1200');
        if (srcset) featured.srcset = '';

        document.querySelectorAll('.main-product__thumb').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  // ============================================================
  // Variant Selection (basic — expand with AJAX cart for production)
  // ============================================================

  function initVariantSelector() {
    const form = document.getElementById('ProductForm');
    if (!form) return;

    const radios = form.querySelectorAll('input[type="radio"]');
    const addBtn = form.querySelector('.main-product__add-to-cart');

    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        const parent = radio.closest('.main-product__option-values');
        if (!parent) return;
        parent.querySelectorAll('.option-swatch').forEach(s => s.classList.remove('is-selected'));
        radio.closest('.option-swatch').classList.add('is-selected');
      });
    });
  }

  // ============================================================
  // Instagram — touch tap-to-toggle overlay
  // ============================================================

  function initInstagramTouch() {
    if (!isTouch) return;

    document.querySelectorAll('.instagram-card__link').forEach(card => {
      const overlay = card.querySelector('.instagram-card__overlay');
      if (!overlay) return;

      card.addEventListener('click', (e) => {
        const isExpanded = card.dataset.expanded === 'true';
        if (!isExpanded) {
          e.preventDefault();
          card.dataset.expanded = 'true';
          overlay.style.opacity = '1';
        } else {
          delete card.dataset.expanded;
        }
      });
    });
  }

  // ============================================================
  // Reduced Motion Listener (runtime change)
  // ============================================================

  function initMotionListener() {
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
      document.documentElement.classList.toggle('motion-reduce', e.matches);
    });
  }

  // ============================================================
  // Init
  // ============================================================

  function init() {
    initScrollReveal();
    initParallax();
    initTilt();
    initHeader();
    initMobileNav();
    initCartDrawer();
    initProductGallery();
    initVariantSelector();
    initInstagramTouch();
    initMotionListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { init };

})();
